type AttributionPayload = {
  firstTimeLanding: string | null;
  firstTimeReferer: string | null;
  firstTimeUtmCampaign: string | null;
  firstTimeUtmContent: string | null;
  firstTimeUtmMedium: string | null;
  firstTimeUtmSource: string | null;
  firstTimeAdgr: string | null;
};

type AttributionRoute = {
  pathname: string;
  search?: string;
  hash?: string;
};

const COOKIE_NAME = 'cb-acc-attr';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // PoC: ~30 days until signup; we will clear on successful registration later.

const truncate = (value: string, maxLen: number): string => {
  if (value.length <= maxLen) return value;
  return value.slice(0, maxLen);
};

const normalizeParam = (value: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const readCookie = (name: string): string | null => {
  // Reads raw cookie value (URI-encoded) for a given cookie name.
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie ? document.cookie.split(';') : [];
  for (const c of cookies) {
    const [cookieName, ...rest] = c.trim().split('=');
    if (cookieName === name) {
      return rest.join('=');
    }
  }
  return null;
};

const buildAttributionPayloadFromHref = (
  href: string,
  referer: string | null
): AttributionPayload => {
  const url = new URL(href);
  const searchParams = url.searchParams;

  const firstTimeLanding = truncate(url.href, 512);
  const firstTimeReferer = referer ? truncate(referer, 512) : null;

  const firstTimeUtmCampaign = normalizeParam(searchParams.get('utm_campaign'));
  const firstTimeUtmContent = normalizeParam(searchParams.get('utm_content'));
  const firstTimeUtmMedium = normalizeParam(searchParams.get('utm_medium'));
  const firstTimeUtmSource = normalizeParam(searchParams.get('utm_source'));
  const firstTimeAdgr = normalizeParam(searchParams.get('adgr'));

  return {
    firstTimeLanding,
    firstTimeReferer,
    firstTimeUtmCampaign,
    firstTimeUtmContent,
    firstTimeUtmMedium,
    firstTimeUtmSource,
    firstTimeAdgr,
  };
};

const isIpAddress = (hostname: string): boolean => {
  return hostname.includes(':') // likely IPv6
  || (hostname.split('.').length === 4) //likely IPv4
};

const buildHrefFromRoute = (route: AttributionRoute): string => {
  const search = route.search ?? '';
  const hash = route.hash ?? '';
  return `${window.location.origin}${route.pathname}${search}${hash}`;
};

const ensureAttributionCookieForHref = (href: string, referer: string | null): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Only set once (PoC: cookie acts as "first time" container).
  const existingRaw = readCookie(COOKIE_NAME);
  if (existingRaw) return;

  const payload = buildAttributionPayloadFromHref(href, referer);
  const encoded = encodeURIComponent(JSON.stringify(payload));

  const secure = window.location.protocol === 'https:';
  const hostname = window.location.hostname;

  const isLocalhost = hostname === 'localhost';
  const looksLikeIp = isIpAddress(hostname);

  const hostnameParts = hostname.split('.');

  const cookieDomain = isLocalhost || looksLikeIp
    ? undefined
    : '.' + hostnameParts.slice(-2).join('.');

  const parts = [`${COOKIE_NAME}=${encoded}`, 'path=/', `SameSite=Lax`, `max-age=${COOKIE_MAX_AGE_SECONDS}`];
  if (cookieDomain) parts.push(`domain=${cookieDomain}`);
  if (secure) parts.push('Secure');

  document.cookie = parts.join('; ');
};

export const ensureAttributionCookie = (): void => {
  ensureAttributionCookieForHref(window.location.href, document.referrer || null);
};

export const ensureAttributionCookieFromRoute = (route: AttributionRoute): void => {
  const href = buildHrefFromRoute(route);
  ensureAttributionCookieForHref(href, document.referrer || null);
};